import React, { useState } from 'react';
import { SupabaseClient, Session } from '@supabase/supabase-js';
import { WorkspaceOption } from '../types';
import { showSuccess, showError, showWarning, Alert } from '../lib/alerts';
import { useNavigate } from 'react-router-dom';

interface CreateTeamProps {
  supabase: SupabaseClient;
  session: Session;
  onTeamCreated?: (membership: WorkspaceOption) => void;
  onBack?: () => void;
  onSignOut?: () => void;
}

export function CreateTeam({ supabase, session, onTeamCreated, onBack, onSignOut }: CreateTeamProps) {
  const [step, setStep] = useState<'choose' | 'create' | 'join'>('choose');
  const [workspaceName, setWorkspaceName] = useState('');
  const [projectName, setProjectName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function createSlug(value: string) {
    const base = value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32);

    const suffix = Math.random().toString(36).slice(2, 8);
    return `${base || 'workspace'}-${suffix}`;
  }

  function createProjectSlug(value: string) {
    const base = value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32);

    const suffix = Math.random().toString(36).slice(2, 8);
    return `${base || 'project'}-${suffix}`;
  }

  const navigate = useNavigate();

  async function handleCreateTeam() {
    if (!workspaceName || !projectName) {
      showWarning('Please fill workspace and project names');
      return;
    }
    setError(null);
    setLoading(true);

    try {
      const { data, error } = await supabase.rpc('create_workspace', {
        workspace_name: workspaceName,
        workspace_slug: createSlug(workspaceName),
        initial_project_name: projectName,
        initial_project_slug: createProjectSlug(projectName),
      });

      if (error) {
        showError(error.message);
        return;
      }

      const workspace = Array.isArray(data) ? data[0] : data;
      const workspaceId = workspace?.workspace_id ?? null;
      const projectId = workspace?.project_id ?? null;

      if (!workspaceId || !projectId) {
        showError('Workspace was created, but the RPC did not return the new IDs.');
        return;
      }

      setWorkspaceName('');
      setProjectName('');

      const { data: inviteData, error: inviteError } = await supabase
        .from('workspace_invites')
        .insert({
          workspace_id: workspaceId,
          project_id: projectId,
          created_by: session.user.id,
        })
        .select('invite_code')
        .single();

      if (inviteError) {
        showWarning('Workspace created, but invite code could not be generated yet.');
      }

      await Alert.fire({
        icon: 'success',
        title: 'Workspace Created!',
        html: inviteData?.invite_code
          ? `<b>${workspaceName}</b> is ready. Team code: <b>${inviteData.invite_code}</b>`
          : `<b>${workspaceName}</b> is ready.`,
        confirmButtonText: 'Go to Dashboard',
      });
      onTeamCreated?.({
        workspaceId,
        workspaceName: workspaceName,
        projectId,
        projectName: projectName,
        role: 'owner',
      });
      navigate('/dashboard/overview', { replace: true });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create team');
    } finally {
      setLoading(false);
    }
  }

  async function handleJoinTeam() {
    if (!inviteCode) {
      showWarning('Please enter an invite code');
      return;
    }
    setError(null);
    setLoading(true);

    try {
      await supabase.from('users').upsert(
        {
          id: session.user.id,
          email: session.user.email ?? '',
          full_name: session.user.user_metadata?.full_name ?? null,
        },
        { onConflict: 'id' }
      );

      // Look up invite code
      const { data: invite, error: inviteError } = await supabase
        .from('workspace_invites')
        .select('workspace_id, project_id')
        .eq('invite_code', inviteCode)
        .eq('is_active', true)
        .single();

      if (inviteError || !invite) {
        showError('Invalid or expired invite code');
        return;
      }

      // Add user to project (idempotent for repeat clicks / existing member)
      const { error: memberError } = await supabase
        .from('project_members')
        .upsert(
          {
            user_id: session.user.id,
            workspace_id: invite.workspace_id,
            project_id: invite.project_id,
            role: 'member',
          },
          {
            onConflict: 'project_id,user_id',
            ignoreDuplicates: true,
          }
        );

      if (memberError) {
        showError(memberError.message);
        return;
      }

      setInviteCode('');
      showSuccess('Joined workspace');
      onTeamCreated?.({
        workspaceId: invite.workspace_id,
        workspaceName: 'Workspace',
        projectId: invite.project_id,
        projectName: 'Project',
        role: 'member',
      });
      navigate('/dashboard/overview', { replace: true });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to join team');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="loading-shell">
      <style>{baseStyles}</style>

      {step === 'choose' && (
        <div className="team-card">
          <div className="onboarding-actions">
            <button type="button" className="ghost-button" onClick={onBack} disabled={!onBack || loading}>
              ← Back
            </button>
            <button type="button" className="ghost-button" onClick={onSignOut} disabled={!onSignOut || loading}>
              Sign Out
            </button>
          </div>

          <div className="status-pill">FounderPulse</div>
          <h2>Get Started</h2>
          <p className="muted">Welcome! Let's set up your first team to start tracking developer productivity.</p>

          <div className="option-grid">
            <button
              onClick={() => setStep('create')}
              className="option-button"
              disabled={loading}
            >
              <div className="option-icon">✨</div>
              <h3>Create Team</h3>
              <p>Start a new workspace and project</p>
            </button>

            <button
              onClick={() => setStep('join')}
              className="option-button"
              disabled={loading}
            >
              <div className="option-icon">👥</div>
              <h3>Join Team</h3>
              <p>Use an invite code to join an existing team</p>
            </button>
          </div>
        </div>
      )}

      {step === 'create' && (
        <div className="team-card">
          <div className="onboarding-actions">
            <button
              type="button"
              onClick={() => {
                setStep('choose');
                setError(null);
              }}
              className="ghost-button"
              disabled={loading}
            >
              ← Back
            </button>
            <button type="button" className="ghost-button" onClick={onSignOut} disabled={!onSignOut || loading}>
              Sign Out
            </button>
          </div>

          <h2>Create Team</h2>
          <p className="muted">Set up a new workspace and project to begin tracking developer analytics.</p>

          <label className="auth-label">
            Workspace Name
            <input
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              type="text"
              placeholder="e.g., My Company"
              required
              disabled={loading}
            />
          </label>

          <label className="auth-label">
            Project Name
            <input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              type="text"
              placeholder="e.g., Backend Services"
              required
              disabled={loading}
            />
          </label>

          {error && <div className="auth-error">{error}</div>}

          <button type="button" className="auth-button" onClick={handleCreateTeam} disabled={loading}>
            {loading ? 'Creating...' : 'Create Team'}
          </button>
        </div>
      )}

      {step === 'join' && (
        <div className="team-card">
          <div className="onboarding-actions">
            <button
              type="button"
              onClick={() => {
                setStep('choose');
                setError(null);
              }}
              className="ghost-button"
              disabled={loading}
            >
              ← Back
            </button>
            <button type="button" className="ghost-button" onClick={onSignOut} disabled={!onSignOut || loading}>
              Sign Out
            </button>
          </div>

          <h2>Join Team</h2>
          <p className="muted">Enter the invite code provided by your team lead to join their workspace.</p>

          <label className="auth-label">
            Invite Code
            <input
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              type="text"
              placeholder="e.g., ABC123XYZ"
              required
              disabled={loading}
            />
          </label>

          {error && <div className="auth-error">{error}</div>}

          <button type="button" className="auth-button" onClick={handleJoinTeam} disabled={loading}>
            {loading ? 'Joining...' : 'Join Team'}
          </button>
        </div>
      )}
    </div>
  );
}

const baseStyles = `
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu',
      'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: linear-gradient(135deg, #0f172a 0%, #1a1f3a 100%);
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .loading-shell {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: linear-gradient(135deg, #0f172a 0%, #1a1f3a 100%);
  }

  .team-card {
    background: rgba(30, 41, 59, 0.9);
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-radius: 12px;
    padding: 48px;
    width: 100%;
    max-width: 480px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(10px);
    position: relative;
  }

  .team-card h2 {
    color: #fff;
    font-size: 28px;
    font-weight: 700;
    margin: 24px 0 12px;
    letter-spacing: -0.5px;
  }

  .team-card p {
    color: #cbd5e1;
    line-height: 1.6;
    margin-bottom: 24px;
  }

  .muted {
    color: #94a3b8 !important;
    font-size: 14px;
  }

  .status-pill {
    display: inline-block;
    background: rgba(34, 197, 94, 0.1);
    border: 1px solid rgba(34, 197, 94, 0.3);
    color: #86efac;
    padding: 6px 16px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    margin-bottom: 16px;
  }

  .back-button {
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 24px;
    transition: color 0.2s;
    padding: 0;
  }

  .onboarding-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
  }

  .ghost-button {
    background: rgba(15, 23, 42, 0.45);
    border: 1px solid rgba(148, 163, 184, 0.2);
    color: #cbd5e1;
    border-radius: 999px;
    padding: 8px 14px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .ghost-button:hover:not(:disabled) {
    border-color: rgba(34, 197, 94, 0.4);
    color: #fff;
  }

  .ghost-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .back-button:hover:not(:disabled) {
    color: #cbd5e1;
  }

  .back-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .option-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 24px;
  }

  .option-button {
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid rgba(148, 163, 184, 0.3);
    border-radius: 8px;
    padding: 24px 16px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    color: inherit;
    font-family: inherit;
  }

  .option-button:hover:not(:disabled) {
    border-color: rgba(34, 197, 94, 0.6);
    background: rgba(34, 197, 94, 0.05);
  }

  .option-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .option-icon {
    font-size: 32px;
  }

  .option-button h3 {
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    margin: 0;
  }

  .option-button p {
    color: #94a3b8;
    font-size: 12px;
    margin: 0;
    text-align: center;
    line-height: 1.4;
  }

  .auth-label {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 20px;
    color: #cbd5e1;
    font-size: 14px;
    font-weight: 500;
  }

  .auth-label input {
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid rgba(148, 163, 184, 0.3);
    border-radius: 8px;
    padding: 12px 14px;
    color: #fff;
    font-size: 14px;
    transition: all 0.2s;
    font-family: inherit;
  }

  .auth-label input:focus {
    outline: none;
    border-color: rgba(34, 197, 94, 0.6);
    background: rgba(15, 23, 42, 0.8);
    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
  }

  .auth-label input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .auth-button {
    width: 100%;
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
    border: none;
    border-radius: 8px;
    padding: 12px 16px;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .auth-button:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(34, 197, 94, 0.3);
  }

  .auth-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .auth-error {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #fca5a5;
    padding: 12px 14px;
    border-radius: 6px;
    font-size: 13px;
    margin-bottom: 16px;
  }
`;
